/**
 * Composable for AWS Amplify client management
 * Centralizes client configuration and provides consistent access patterns
 */

import { generateClient } from 'aws-amplify/data';
import { getCurrentUser, fetchUserAttributes } from 'aws-amplify/auth';

// Cache clients to avoid recreating them
let defaultClient = null;
let authClient = null;

/**
 * Get or create the default Amplify client (IAM/Identity Pool auth)
 * Uses IAM for both authenticated and guest users
 */
function getDefaultClient() {
  if (!defaultClient) {
    defaultClient = generateClient({ authMode: 'iam' });
  }
  return defaultClient;
}

/**
 * Get or create the authenticated Amplify client (user pool auth)
 */
function getAuthClient() {
  if (!authClient) {
    authClient = generateClient({ authMode: 'userPool' });
  }
  return authClient;
}

/**
 * Main composable function
 */
export function useAmplifyClient() {
  const client = getDefaultClient();
  const authenticatedClient = getAuthClient();

  /**
   * Get current authenticated user with error handling
   */
  const getCurrentUserSafe = async () => {
    try {
      const user = await getCurrentUser();
      return { user, error: null };
    } catch (error) {
      return { user: null, error };
    }
  };

  /**
   * Get user attributes with error handling
   */
  const getUserAttributesSafe = async () => {
    try {
      const attributes = await fetchUserAttributes();
      return { attributes, error: null };
    } catch (error) {
      return { attributes: null, error };
    }
  };

  /**
   * Check if user is authenticated
   */
  const checkAuthStatus = async () => {
    const { user } = await getCurrentUserSafe();
    return !!user;
  };

  /**
   * Get user information (user + attributes) in one call
   */
  const getUserInfo = async () => {
    const { user, error: userError } = await getCurrentUserSafe();
    
    if (!user) {
      return { user: null, attributes: null, error: userError };
    }

    const { attributes, error: attrError } = await getUserAttributesSafe();
    
    return {
      user,
      attributes,
      error: attrError
    };
  };

  /**
   * Execute GraphQL query with error handling
   */
  const executeQuery = async (query, variables = {}, useAuth = false) => {
    try {
      const targetClient = useAuth ? authenticatedClient : client;
      const result = await targetClient.graphql({
        query,
        variables
      });
      return { data: result.data, error: null };
    } catch (error) {
      console.error('GraphQL query failed:', error);
      return { data: null, error };
    }
  };

  /**
   * Execute mutation with error handling
   */
  const executeMutation = async (mutation, variables = {}, useAuth = false) => {
    try {
      const targetClient = useAuth ? authenticatedClient : client;
      const result = await targetClient.graphql({
        query: mutation,
        variables
      });
      return { data: result.data, error: null };
    } catch (error) {
      console.error('GraphQL mutation failed:', error);
      return { data: null, error };
    }
  };

  /**
   * Work with models (the modern Amplify way)
   */
  const getModels = (useAuth = false) => {
    const targetClient = useAuth ? authenticatedClient : client;
    return targetClient.models;
  };

  return {
    // Raw clients
    client,
    authenticatedClient,
    
    // Authentication helpers
    getCurrentUser: getCurrentUserSafe,
    getUserAttributes: getUserAttributesSafe,
    getUserInfo,
    checkAuthStatus,
    
    // GraphQL helpers
    executeQuery,
    executeMutation,
    
    // Model access
    models: getModels(false), // Default to public access
    authModels: getModels(true), // Authenticated access
    getModels,
    
    // Legacy support
    getCurrentUser: getCurrentUserSafe,
    fetchUserAttributes: getUserAttributesSafe
  };
}

/**
 * Simple version for basic usage
 */
export function useAmplify() {
  return {
    client: getDefaultClient(),
    authClient: getAuthClient()
  };
}

// Export individual functions for advanced usage
export {
  getDefaultClient,
  getAuthClient,
  getCurrentUser,
  fetchUserAttributes
};